/**
 * Tap tempo (W2-08 timing; DESIGN.md §5.2.2): tap along with B or the Tap button; the running average shows after
 * 4 taps, a pause of 2 s starts a new series, and the count and the spread (+- BPM) say how steady the taps were.
 * The tapped tempo is applied with Use (one undoable edit), never while tapping. Presentational: the series lives
 * in the timing session (the B command and this button share it) or in the wizard step.
 */

import { Button, Kbd, Tooltip } from '../../ui';
import { m } from './timing.messages';
import type { TimingText } from './timingHooks';
import { TAP_MIN, type TapReading } from './timingModel';
import s from './Timing.module.css';

export interface TapTempoProps {
  readonly text: TimingText;
  readonly reading: TapReading;
  /** Shortcut shown on the button (null: none). */
  readonly chord: string | null;
  onTap(): void;
  onUse(bpm: number): void;
  onReset(): void;
}

export function TapTempo(props: TapTempoProps) {
  const { text, reading } = props;
  const status =
    reading.count === 0
      ? text.t(m.tapIdle)
      : reading.bpm === null
        ? text.t(m.tapCounting, { count: reading.count })
        : text.t(m.tapResult, { count: reading.count, bpm: reading.bpm });
  const bpm = reading.bpm;
  return (
    <div className={s.stack}>
      <div className={s.row}>
        <Tooltip content={text.t(m.tapTip)}>
          <Button
            className={s.tapButton}
            icon="Hand"
            variant="primary"
            onClick={() => {
              props.onTap();
            }}
            data-testid="timing-tap"
          >
            {text.t(m.tap)}
            {props.chord !== null && (
              <>
                {' '}
                <Kbd chord={props.chord} />
              </>
            )}
          </Button>
        </Tooltip>
        <span className={s.tapDots} aria-hidden="true">
          {Array.from({ length: TAP_MIN }, (_, i) => (
            <span key={i} className={s.tapDot} data-on={reading.count > i} />
          ))}
        </span>
      </div>
      <p className={`${s.tapReading} ${s.help}`} role="status" aria-live="polite" data-testid="timing-tap-reading">
        {status}
        {reading.spreadBpm !== null && <> ({text.t(m.tapSpread, { spread: reading.spreadBpm })})</>}
      </p>
      {reading.count > 0 && (
        <div className={s.rowTight}>
          {bpm !== null && (
            <Button
              size="sm"
              onClick={() => {
                props.onUse(Math.round(bpm * 100) / 100);
              }}
              aria-label={text.t(m.tapUseLabel, { bpm })}
            >
              {text.t(m.tapUse)}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon="RotateCcw"
            onClick={() => {
              props.onReset();
            }}
          >
            {text.t(m.tapReset)}
          </Button>
        </div>
      )}
      {reading.count > 0 && <p className={s.subtle}>{text.t(m.tapResetHint)}</p>}
    </div>
  );
}
